import { useState, useEffect } from 'react'

export interface ZebraPrinter {
  readonly uid: string
  readonly name: string
  readonly connection: 'usb' | 'bluetooth' | 'network' | 'driver'
}

interface ZebraDevice {
  uid: string
  name: string
  connection: string
  send(data: string, successCb: () => void, errorCb: (err: unknown) => void): void
}

interface BrowserPrintSdk {
  getLocalDevices(
    deviceType: string | null,
    successCb: (devices: ZebraDevice[]) => void,
    errorCb: (err: unknown) => void,
  ): void
  getDefaultDevice(deviceType: string, successCb: (device: ZebraDevice) => void, errorCb?: (err: unknown) => void): void
}

declare global {
  interface Window {
    BrowserPrint?: BrowserPrintSdk
  }
}

type SdkState = { available: boolean; error: string | null; sdk: BrowserPrintSdk | null }

let singleton: SdkState | null = null
let listeners: Array<(state: SdkState) => void> = []

function notifyListeners(state: SdkState) {
  singleton = state
  listeners.forEach((fn) => fn(state))
}

let pollStarted = false

function startPoll() {
  if (pollStarted) return
  pollStarted = true

  const INTERVAL_MS = 100
  const TIMEOUT_MS = 3000
  const maxTicks = TIMEOUT_MS / INTERVAL_MS
  let ticks = 0

  const id = setInterval(() => {
    ticks++
    const sdk = window.BrowserPrint
    if (sdk) {
      clearInterval(id)
      notifyListeners({ available: true, error: null, sdk })
      return
    }
    if (ticks >= maxTicks) {
      clearInterval(id)
      notifyListeners({
        available: false,
        error:
          'Zebra Browser Print is not running on this machine; install it from https://www.zebra.com/browserprint and refresh the page.',
        sdk: null,
      })
    }
  }, INTERVAL_MS)
}

function normalizeConnection(raw: string): ZebraPrinter['connection'] {
  const c = raw.toLowerCase()
  if (c === 'usb') return 'usb'
  if (c === 'bluetooth') return 'bluetooth'
  if (c === 'network') return 'network'
  return 'driver'
}

function getLocalDevicesAsync(sdk: BrowserPrintSdk): Promise<ZebraPrinter[]> {
  return new Promise((resolve, reject) => {
    sdk.getLocalDevices(
      null,
      (devices) => {
        resolve(
          devices.map((d) => ({
            uid: d.uid,
            name: d.name,
            connection: normalizeConnection(d.connection),
          })),
        )
      },
      (err) => reject(new Error(err instanceof Error ? err.message : String(err))),
    )
  })
}

function sendAsync(device: ZebraDevice, zpl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    device.send(
      zpl,
      () => resolve(),
      (err) => reject(new Error(err instanceof Error ? err.message : String(err))),
    )
  })
}

function findDeviceAsync(sdk: BrowserPrintSdk, printerUid: string): Promise<ZebraDevice> {
  return new Promise((resolve, reject) => {
    sdk.getLocalDevices(
      null,
      (devices) => {
        const match = devices.find((d) => d.uid === printerUid)
        if (match) {
          resolve(match)
        } else {
          reject(new Error(`Zebra printer with uid '${printerUid}' not found`))
        }
      },
      (err) => reject(new Error(err instanceof Error ? err.message : String(err))),
    )
  })
}

interface UseZebraBrowserPrintResult {
  readonly available: boolean
  readonly error: string | null
  readonly printers: readonly ZebraPrinter[]
  readonly print: (zpl: string, printerUid: string) => Promise<void>
  readonly refreshPrinters: () => Promise<void>
}

export function useZebraBrowserPrint(): UseZebraBrowserPrintResult {
  // Lazy initializers capture singleton state at mount without a synchronous
  // setState in the effect body (avoids react-hooks/set-state-in-effect).
  const [state, setState] = useState<SdkState>(() => singleton ?? { available: false, error: null, sdk: null })
  const [printers, setPrinters] = useState<readonly ZebraPrinter[]>([])

  useEffect(() => {
    // If singleton already resolved before this mount, seed printers (async)
    // and skip listener registration.
    if (singleton) {
      if (singleton.sdk) {
        getLocalDevicesAsync(singleton.sdk)
          .then(setPrinters)
          .catch(() => {})
      }
      return
    }

    const listener = (s: SdkState) => {
      setState(s)
      if (s.sdk) {
        getLocalDevicesAsync(s.sdk)
          .then(setPrinters)
          .catch(() => {})
      }
    }
    listeners.push(listener)
    startPoll()

    return () => {
      listeners = listeners.filter((l) => l !== listener)
    }
  }, [])

  async function print(zpl: string, printerUid: string): Promise<void> {
    const sdk = singleton?.sdk
    if (!sdk) throw new Error('Zebra Browser Print SDK is not available')
    const device = await findDeviceAsync(sdk, printerUid)
    await sendAsync(device, zpl)
  }

  async function refreshPrinters(): Promise<void> {
    const sdk = singleton?.sdk
    if (!sdk) return
    const found = await getLocalDevicesAsync(sdk)
    setPrinters(found)
  }

  return { available: state.available, error: state.error, printers, print, refreshPrinters }
}
